import * as Accordion from "@radix-ui/react-accordion";
import { useRefinementList } from "react-instantsearch";
import { FACET_FETCH_LIMIT } from "@/components/Search/constants";
import { useSearchMessages } from "@/i18n/hooks/useSearchMessages";
import LearningTypeSubtitle from "@/components/Search/utils/LearningTypeSubtitle";
import useHandleSearchParams from "@/hooks/useHandleSearchParams";
import getSortedRefinementItems from "@/components/Search/utils/getSortedRefinementItems";
import { useState } from "react";
import { useSearchIndex } from "../SearchContextProvider";
import Icon from "@/components/icons";
// import { ChevronDownIcon } from "@radix-ui/react-icons";
import { cn } from "@/lib/utils";
import { FieldSelected } from "@/hooks/eventing/types/redventures.usertracking.v3.FieldSelected";
import {
  getCorrelationID,
  getPageType,
  getWebContext,
} from "@/utils/tagular/helpers";
import useFormEvents from "@/hooks/eventing/useFormtEvents";
import useTranslations from "@/i18n/useTranslations";

const ModalAccordionItem = ({ filterCategory }: { filterCategory: string }) => {
  const t = useTranslations("Common");
  const { getFilterCategoryName, getDisplayLabel, getSearchFilterAriaLabel } =
    useSearchMessages();
  const { searchIndex } = useSearchIndex();
  const { setSearchParam, removeSearchParam } = useHandleSearchParams();
  const [searchQuery, setSearchQuery] = useState("");
  const { items, refine } = useRefinementList({
    attribute: filterCategory,
    sortBy: ["isRefined", "count:desc"],
    limit: FACET_FETCH_LIMIT,
    showMore: false,
    escapeFacetValues: true,
  });
  const { fieldSelected } = useFormEvents({});

  const handleRefinementClick = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const webBase = getWebContext();
    const formEvent: FieldSelected = {
      correlationId: getCorrelationID(),
      userInputField: {
        fieldName: event.target.value,
        fieldLabel: getFilterCategoryName(filterCategory),
        fieldType: "search-filters-modal",
        fieldValue: event.target.checked ? "Selected" : "Deselected",
      },
      webContext: {
        ...webBase,
        userAgent: webBase?.userAgent ?? undefined,
        page: {
          title: document.title,
          url: window.location.href,
          pageType: getPageType(),
        },
      },
      "@type": "redventures.usertracking.v3.FieldSelected",
    };

    fieldSelected(formEvent);

    const value = event.target.value;
    refine(value);

    if (event.target.checked) {
      setSearchParam(filterCategory, value);
    } else {
      removeSearchParam(filterCategory, value);
    }
  };

  const handleClearSearch = () => {
    setSearchQuery("");
  };

  const numberOfActiveItems = items.filter((item) => item.isRefined).length;
  const selectCounter =
    numberOfActiveItems > 0 ? ` (${numberOfActiveItems})` : "";

  const filteredItems = getSortedRefinementItems(items).filter((item) =>
    item.label.toLowerCase().includes(searchQuery.toLowerCase()),
  );

  if (filteredItems.length === 0) {
    return null;
  }

  return (
    <Accordion.Item value={filterCategory} className="border-b">
      <Accordion.Header className="flex m-0">
        <Accordion.Trigger
          className={cn(
            "[&[data-state=open]]:bg-putty-200 flex flex-1 py-2 px-5 items-center justify-between text-lg text-left font-bold transition-all [&[data-state=open]>svg]:rotate-180",
            "text-[1.125rem] h-12 font-semibold pr-4",
          )}
        >
          {getFilterCategoryName(filterCategory)}
          {selectCounter}
          <Icon
            type="keyboard-arrow-down-material"
            pathProps={{
              fill: "#000000ff",
            }}
            svgProps={{
              fill: "#000000ff",
              className: `${cn(
                "h-5 w-5 shrink-0 text-brand font-bold transition-transform duration-200",
                "text-gray-700 color-gray-700",
              )}`,
            }}
          />
        </Accordion.Trigger>
      </Accordion.Header>
      <Accordion.Content
        className={cn(
          "overflow-hidden text-sm data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down",
          "relative -mx-5",
        )}
      >
        <div className="px-5">
          {items.length > 15 && (
            <div className="w-full mb-0 relative">
              <input
                type="search"
                placeholder={getSearchFilterAriaLabel(filterCategory)}
                aria-label={getSearchFilterAriaLabel(filterCategory)}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="select-none w-full px-5 py-3 border-0 border-b border-t focus:border-gray-200 text-gray-600 border-gray-200 text-base pr-12 text-sm"
              />
              <div className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400">
                {searchQuery ? (
                  <button
                    onClick={handleClearSearch}
                    className="hover:text-gray-600"
                  >
                    <Icon
                      type="close-material"
                      pathProps={{
                        fill: "#000000ff",
                        width: "17px",
                        height: "17px",
                      }}
                      svgProps={{
                        fill: "#000000ff",
                        width: "17px",
                        height: "17px",
                      }}
                    />
                  </button>
                ) : (
                  <Icon
                    type="magnifying-glass"
                    pathProps={{
                      fill: "#000000ff",
                      width: "17px",
                      height: "17px",
                    }}
                    svgProps={{
                      fill: "#000000ff",
                      width: "17px",
                      height: "17px",
                    }}
                  />
                )}
              </div>
            </div>
          )}
          <div className="py-2 gap-2">
            {filteredItems.length > 0 ? (
              filteredItems.map((item) => (
                <div key={item.label} className="px-5">
                  <div className="py-2 flex justify-start items-start">
                    <input
                      type="checkbox"
                      id={item.value}
                      value={item.value}
                      checked={item.isRefined}
                      onChange={handleRefinementClick}
                      className="border-2 mr-2 border-gray-700 min-w-4 h-5 w-5 cursor-pointer"
                    />
                    <div className="flex justify-start items-start flex-col">
                      <label
                        htmlFor={item.value}
                        className="text-gray-700 flex items-center mb-0 leading-5"
                      >
                        {getDisplayLabel(item.label, filterCategory)}
                      </label>
                      <LearningTypeSubtitle
                        filterCategory={filterCategory}
                        itemValue={item.value}
                        indexName={searchIndex}
                      />
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="py-2 px-5 text-gray-600">
                {t("noOptionsFound")}
              </div>
            )}
          </div>
        </div>
      </Accordion.Content>
    </Accordion.Item>
  );
};

export default ModalAccordionItem;
